import { impersonationRef } from "@client/ImperativeState";
import type { QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * Identity scoping for the startup prefetches.
 *
 * Artifact query keys carry no identity by design — impersonation rides on a
 * request header, so one key serves whoever the effective viewer is, and
 * TRPCProvider invalidates (desktop) or clears (iPad) the whole cache when the
 * viewer changes. That works for a request whose response lands before the
 * switch, but a startup prefetch is a *chain*: it fetches, then derives a
 * second request from the first response. A chain can straddle a switch, and
 * responses that were fetched as the previous viewer then land in the cache
 * the new viewer reads — after the global invalidation has already run.
 *
 * So a chain records every key it warms and drops them all if the effective
 * viewer moved while it ran. The mounted hooks then fetch normally as the new
 * viewer; nothing is lost but the head start.
 */
export interface IdentityScopedPrefetchGuard {
  /** The effective viewer when the chain started (null = not impersonating). */
  startedAs: string | null;
  /** Record a key this chain is about to warm; returns it for inline use. */
  warm: (key: QueryKey) => QueryKey;
  viewerChanged: () => boolean;
  /**
   * Drop everything warmed so far if the viewer changed. Returns true when
   * it did, so callers can stop the chain rather than warm more entries for
   * a viewer who is no longer looking.
   */
  discardIfViewerChanged: () => boolean;
}

export function beginIdentityScopedPrefetch(
  queryClient: QueryClient,
): IdentityScopedPrefetchGuard {
  const startedAs = impersonationRef.current;
  const warmedKeys: QueryKey[] = [];

  const viewerChanged = () => impersonationRef.current !== startedAs;

  return {
    startedAs,
    warm(key: QueryKey): QueryKey {
      warmedKeys.push(key);
      return key;
    },
    viewerChanged,
    discardIfViewerChanged(): boolean {
      if (!viewerChanged()) return false;
      for (const key of warmedKeys) {
        queryClient.removeQueries({ queryKey: key });
      }
      return true;
    },
  };
}
